/**
 * "/" answers in the composer: find the "/word" being typed at the caret and rank the library answers for it.
 * Pure functions (unit-tested in test/library.spec.ts).
 */
import type { CannedItem } from '../api/types'

export interface SlashToken {
  /** Index of the "/" in the text. */
  start: number
  /** Caret position (end of the word being typed). */
  end: number
  /** What was typed after "/" (may be empty, may be Thai). */
  query: string
}

/** The "/word" that ends at the caret, when "/" starts a word (line start or after a space). */
export function slashAt(text: string, caret: number): SlashToken | null {
  const before = text.slice(0, caret)
  const m = /(^|\s)\/([^\s/]{0,32})$/.exec(before)
  if (!m) return null
  const query = m[2] ?? ''
  return { start: caret - query.length - 1, end: caret, query }
}

/** Text with the "/word" removed (the answer goes where it was). */
export function withoutToken(text: string, t: SlashToken): string {
  return text.slice(0, t.start) + text.slice(t.end)
}

const norm = (s: string): string => s.normalize('NFC').toLowerCase()

/** Letters of `q` appear in `s` in order (e.g. "mnu" → "menu"). */
function subsequence(q: string, s: string): boolean {
  let i = 0
  for (const ch of s) if (ch === q[i]) i++
  return i === q.length
}

function score(item: CannedItem, q: string): number {
  const sc = norm(item.shortcut)
  const title = norm(item.title)
  if (sc === q) return 100
  if (sc.startsWith(q)) return 80
  if (title.startsWith(q)) return 60
  if (sc.includes(q)) return 50
  if (title.includes(q)) return 40
  if (norm(item.text || item.body).includes(q)) return 25
  if (q.length >= 2 && subsequence(q, sc)) return 15
  return 0
}

/** Active answers matching the query, best first (ties: most used, then the library order). */
export function rankCanned(items: CannedItem[], query: string): CannedItem[] {
  const q = norm(query.trim())
  const live = items.filter((i) => i.is_active)
  if (!q) return live
  return live
    .map((item, order) => ({ item, order, s: score(item, q) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || b.item.use_count - a.item.use_count || a.order - b.order)
    .map((x) => x.item)
}

/** First line of an answer for the picker, at most `max` characters (the rest is summarised with "…"). */
export function firstLine(text: string, max = 110): string {
  const line = (text.split('\n').find((l) => l.trim()) ?? '').trim()
  return line.length > max ? `${line.slice(0, max).trimEnd()}…` : line
}
